import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, KeyboardEvent, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../cn'
import type { CollectionFilterDef, CollectionFilterValue, CollectionOperator, CollectionOperatorValue, UseCollectionResult } from './useCollection'

export interface FilterEditorDialogProps<RowT> {
  collection: UseCollectionResult<RowT>
  filter: CollectionFilterDef<RowT>
  open: boolean
  onClose: () => void
  anchorRef?: RefObject<HTMLElement>
}

const PANEL_WIDTH = 288
const PANEL_GUTTER = 8

interface PanelPosition {
  top: number
  left: number
}

export function FilterEditorDialog<RowT>({ collection, filter, open, onClose, anchorRef }: FilterEditorDialogProps<RowT>) {
  const [draft, setDraft] = useState<CollectionFilterValue | undefined>(collection.state.filters[filter.id])
  const panelRef = useRef<HTMLDivElement>(null)
  const [portalPosition, setPortalPosition] = useState<PanelPosition | null>(null)

  const updatePortalPosition = useCallback(() => {
    const anchor = anchorRef?.current
    if (!anchor || typeof window === 'undefined') return
    setPortalPosition(getPanelPosition(anchor))
  }, [anchorRef])

  useEffect(() => {
    if (!open) return
    setDraft(collection.state.filters[filter.id])
    requestAnimationFrame(() => {
      updatePortalPosition()
      panelRef.current?.querySelector<HTMLElement>('input, select, button')?.focus()
    })
  }, [collection.state.filters, filter.id, open, updatePortalPosition])

  useEffect(() => {
    if (!open || !anchorRef?.current || typeof window === 'undefined') {
      setPortalPosition(null)
      return
    }
    updatePortalPosition()
    window.addEventListener('resize', updatePortalPosition)
    window.addEventListener('scroll', updatePortalPosition, true)
    return () => {
      window.removeEventListener('resize', updatePortalPosition)
      window.removeEventListener('scroll', updatePortalPosition, true)
    }
  }, [anchorRef, open, updatePortalPosition])

  if (!open) return null

  function apply() {
    collection.setFilter(filter.id, draft)
    onClose()
  }

  const portalTarget = typeof document === 'undefined' ? null : document.body
  const anchor = anchorRef?.current ?? null
  const usePortal = Boolean(portalTarget)
  const style = getPanelStyle(anchor ? portalPosition ?? getPanelPosition(anchor) : getFallbackPanelPosition())
  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label={`${filter.label} filter`}
      onKeyDown={(event) => trapDialogKeys(event, panelRef.current, onClose)}
      style={style}
      className={cn(
        'z-dropdown w-72 rounded-control border border-line bg-white p-3 text-sm shadow-pop animate-fade-in motion-reduce:animate-none',
        usePortal ? 'fixed' : 'absolute right-0 mt-2',
      )}
    >
      <div className="mb-3 flex items-center justify-between gap-3 border-b border-line pb-2">
        <p className="m-0 text-sm font-semibold text-strong">{filter.label}</p>
        <button type="button" onClick={onClose} aria-label="Close filter" className="min-h-8 rounded px-2 text-ink-muted hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus">x</button>
      </div>
      <FilterEditor filter={filter} value={draft} onChange={setDraft} />
      <div className="mt-4 flex items-center justify-end gap-2">
        <button type="button" onClick={() => setDraft(undefined)} className="min-h-tap rounded-control px-3 text-xs font-medium text-ink-muted hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus">Clear</button>
        <button type="button" onClick={onClose} className="min-h-tap rounded-control border border-line px-3 text-xs font-medium text-ink-body hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus">Cancel</button>
        <button type="button" onClick={apply} className="min-h-tap rounded-control bg-mustard px-3 text-xs font-semibold text-ink hover:bg-mustard-600 focus-visible:outline-none focus-visible:shadow-focus">Apply</button>
      </div>
    </div>
  )

  return usePortal && portalTarget ? createPortal(panel, portalTarget) : panel
}

export interface FilterEditorProps<RowT> {
  filter: CollectionFilterDef<RowT>
  value: CollectionFilterValue | undefined
  onChange: (value: CollectionFilterValue | undefined) => void
}

export function FilterEditor<RowT>({ filter, value, onChange }: FilterEditorProps<RowT>) {
  if (filter.operators?.length) {
    return <OperatorFilterEditor filter={filter} value={value} onChange={onChange} />
  }
  if (filter.type === 'text') {
    return <input type="text" value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value)} placeholder={filter.placeholder} className={fieldClass} />
  }
  if (filter.type === 'select') {
    return (
      <select value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value || undefined)} className={fieldClass}>
        <option value="">{filter.placeholder ?? 'Any'}</option>
        {filter.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    )
  }
  if (filter.type === 'multiSelect') {
    const selected = Array.isArray(value) ? value : []
    return (
      <div className="flex max-h-56 flex-col gap-1 overflow-auto">
        {filter.options.map((option) => (
          <label key={option.value} className="flex min-h-tap items-center gap-2 rounded-control px-2 text-ink-body hover:bg-whisper">
            <input
              type="checkbox"
              checked={selected.includes(option.value)}
              onChange={(event) => onChange(event.target.checked ? [...selected, option.value] : selected.filter((item) => item !== option.value))}
              className="size-4 rounded border-line text-link focus-visible:outline-none focus-visible:shadow-focus"
            />
            {option.label}
          </label>
        ))}
      </div>
    )
  }
  if (filter.type === 'boolean') {
    return (
      <select value={typeof value === 'boolean' ? String(value) : ''} onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value === 'true')} className={fieldClass}>
        <option value="">Any</option>
        <option value="true">{filter.trueLabel ?? 'Yes'}</option>
        <option value="false">{filter.falseLabel ?? 'No'}</option>
      </select>
    )
  }
  return <RangeEditor type={filter.type} value={value} onChange={onChange} />
}

function OperatorFilterEditor<RowT>({ filter, value, onChange }: FilterEditorProps<RowT>) {
  const current = toOperatorValue(filter, value)
  const operators = filter.operators ?? [defaultOperator(filter)]
  const operator = current.op
  const hidesValue = operator === 'empty' || operator === 'notEmpty'

  function setOperator(nextOperator: CollectionOperator) {
    if (nextOperator === 'empty' || nextOperator === 'notEmpty') {
      onChange({ op: nextOperator })
      return
    }
    onChange({ op: nextOperator, value: current.value })
  }

  function setValue(nextValue: string | string[] | undefined) {
    onChange({ op: operator, value: nextValue })
  }

  return (
    <div className="space-y-2">
      <select aria-label={`${filter.label} operator`} value={operator} onChange={(event) => setOperator(event.target.value as CollectionOperator)} className={fieldClass}>
        {operators.map((op) => <option key={op} value={op}>{operatorOptionLabel(op)}</option>)}
      </select>
      {hidesValue ? null : <OperatorValueEditor filter={filter} value={current.value} onChange={setValue} />}
    </div>
  )
}

function OperatorValueEditor<RowT>({ filter, value, onChange }: { filter: CollectionFilterDef<RowT>; value: string | string[] | undefined; onChange: (value: string | string[] | undefined) => void }) {
  if (filter.type === 'text') {
    return <input type="text" value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value)} placeholder={filter.placeholder} className={fieldClass} />
  }
  if (filter.type === 'select') {
    const selected = Array.isArray(value) ? value[0] : value ?? ''
    return (
      <select value={selected} onChange={(event) => onChange(event.target.value || undefined)} className={fieldClass}>
        <option value="">{filter.placeholder ?? 'Any'}</option>
        {filter.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    )
  }
  if (filter.type === 'multiSelect') {
    const selected = Array.isArray(value) ? value : value ? [value] : []
    return (
      <div className="flex max-h-56 flex-col gap-1 overflow-auto">
        {filter.options.map((option) => (
          <label key={option.value} className="flex min-h-tap items-center gap-2 rounded-control px-2 text-ink-body hover:bg-whisper">
            <input
              type="checkbox"
              checked={selected.includes(option.value)}
              onChange={(event) => onChange(event.target.checked ? [...selected, option.value] : selected.filter((item) => item !== option.value))}
              className="size-4 rounded border-line text-link focus-visible:outline-none focus-visible:shadow-focus"
            />
            {option.label}
          </label>
        ))}
      </div>
    )
  }
  return null
}

function toOperatorValue<RowT>(filter: CollectionFilterDef<RowT>, value: CollectionFilterValue | undefined): CollectionOperatorValue {
  if (isOperatorValue(value)) return value
  const op = filter.operators?.[0] ?? defaultOperator(filter)
  if (op === 'empty' || op === 'notEmpty') return { op }
  return { op, value: normalizeOperatorValue(value) }
}

function normalizeOperatorValue(value: CollectionFilterValue | undefined): string | string[] | undefined {
  if (typeof value === 'string' || Array.isArray(value)) return value
  if (typeof value === 'boolean') return String(value)
  return undefined
}

function defaultOperator<RowT>(filter: CollectionFilterDef<RowT>): CollectionOperator {
  if (filter.type === 'select' || filter.type === 'multiSelect') return 'anyOf'
  return 'contains'
}

function isOperatorValue(value: CollectionFilterValue | undefined): value is CollectionOperatorValue {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && 'op' in value
}

function operatorOptionLabel(op: CollectionOperator): string {
  if (op === 'equals' || op === 'anyOf') return 'Is'
  if (op === 'startsWith') return 'Starts with'
  if (op === 'notContains') return 'Does not contain'
  if (op === 'noneOf') return 'Is not'
  if (op === 'empty') return 'Is empty'
  if (op === 'notEmpty') return 'Is not empty'
  return 'Contains'
}

function RangeEditor({ type, value, onChange }: { type: 'dateRange' | 'numberRange'; value: CollectionFilterValue | undefined; onChange: (value: CollectionFilterValue | undefined) => void }) {
  const range = useMemo<Record<string, unknown>>(() => (typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : {}), [value])
  const inputType = type === 'dateRange' ? 'date' : 'number'
  const firstKey = type === 'dateRange' ? 'from' : 'min'
  const secondKey = type === 'dateRange' ? 'to' : 'max'

  function setPart(key: string, nextValue: string) {
    const parsed = type === 'numberRange' && nextValue !== '' ? Number(nextValue) : nextValue
    onChange({ ...range, [key]: nextValue === '' ? undefined : parsed })
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      <label className="text-xs font-medium text-ink-muted">
        {firstKey}
        <input type={inputType} value={String(range[firstKey] ?? '')} onChange={(event) => setPart(firstKey, event.target.value)} className={cn(fieldClass, 'mt-1')} />
      </label>
      <label className="text-xs font-medium text-ink-muted">
        {secondKey}
        <input type={inputType} value={String(range[secondKey] ?? '')} onChange={(event) => setPart(secondKey, event.target.value)} className={cn(fieldClass, 'mt-1')} />
      </label>
    </div>
  )
}

const fieldClass = 'min-h-tap w-full rounded-control border border-line bg-white px-3 text-sm text-ink-body placeholder:text-ink-faint transition-colors duration-fast ease-out-soft focus-visible:outline-none focus-visible:shadow-focus'

function getPanelPosition(anchor: HTMLElement): PanelPosition {
  const rect = anchor.getBoundingClientRect()
  const viewportWidth = document.documentElement.clientWidth || window.innerWidth
  const maxLeft = Math.max(PANEL_GUTTER, viewportWidth - PANEL_WIDTH - PANEL_GUTTER)
  const left = Math.min(Math.max(PANEL_GUTTER, rect.right - PANEL_WIDTH), maxLeft)
  return { top: rect.bottom + PANEL_GUTTER, left }
}

function getFallbackPanelPosition(): PanelPosition {
  return { top: PANEL_GUTTER, left: PANEL_GUTTER }
}

function getPanelStyle(position: PanelPosition): CSSProperties {
  return {
    top: position.top,
    left: position.left,
    width: PANEL_WIDTH,
  }
}

function trapDialogKeys(event: KeyboardEvent, panel: HTMLDivElement | null, onClose: () => void) {
  if (event.key === 'Escape') {
    event.preventDefault()
    onClose()
    return
  }
  if (event.key !== 'Tab' || !panel) return
  const focusable = Array.from(panel.querySelectorAll<HTMLElement>('button, input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter((item) => !item.hasAttribute('disabled'))
  if (focusable.length === 0) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}
